<template>
    <div class="ticket-wrapper">
      <div class="ticket ticket--high-contrast-print" :class="{ 'ticket--duplicate': duplicate }">
        <div v-if="duplicate" class="ticket-watermark">DUPLICATA</div>
        <div v-if="isCreditTicket" class="ticket-watermark ticket-watermark--credit">À CRÉDIT</div>
        <!-- En-tête -->
        <div class="ticket-header text-center">
          <img
            v-if="ticketLogo"
            :src="ticketLogo"
            alt="Logo"
            class="ticket-logo-image"
            @error="handleTicketLogoError"
          />
          <div class="ticket-logo" v-else-if="logoText">
            {{ logoText }}
          </div>
          <h5 class="ticket-title mb-1">{{ ticketBusinessName }}</h5>
          <div v-if="ticketLegalName" class="ticket-legal-name">{{ ticketLegalName }}</div>
          <p class="ticket-subtitle mb-0">
            <span v-if="ticketAddress">{{ ticketAddress }}<br /></span>
            <span v-if="ticketPhone">{{ ticketPhone }}</span>
            <span v-if="ticketEmail"><br />{{ ticketEmail }}</span>
            <span v-if="ticketTaxId"><br />NIF : {{ ticketTaxId }}</span>
          </p>
          <hr class="my-2" />
          <p class="ticket-meta mb-0">
            Ticket n° <strong>{{ sale.reference }}</strong><br />
            <span v-if="sale.daily_position">Position : <strong>{{ sale.daily_position }}</strong><br /></span>
            Date : {{ $format.date(sale.created_at) }}<span v-if="sale.ticket_last_printed_at">&nbsp;{{ formatPrintedTime(sale.ticket_last_printed_at) }}</span><br />
            <span v-if="cashierName">
              Caissier : {{ cashierName }}
            </span>
            <span v-if="cashRegisterName">
              <br />Caisse : {{ cashRegisterName }}
            </span>
          </p>
        </div>
  
        <hr class="my-2" />
  
        <!-- Lignes -->
        <div class="ticket-body">
          <table class="ticket-table">
            <thead>
              <tr>
                <th class="text-start">Désignation</th>
                <th class="text-end">Qté</th>
                <th class="text-end">PU</th>
                <th class="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in activeItems" :key="index">
                <td class="text-start">
                  {{ item.product_name }}
                  <div v-if="isPromotionLine(item)" class="ticket-promo-label">
                    {{ item.price_source === 'cashier_promotion' ? 'Promo caisse' : 'Promotion' }}
                  </div>
                  <div v-if="isFormulaLine(item)" class="ticket-promo-label">
                    Formule {{ item.formula_quantity }} pour {{ formatLineAmount(item.formula_price || 0) }}
                  </div>
                  <div v-if="item.sale_price_coefficient !== null && item.sale_price_coefficient !== undefined" class="ticket-coefficient-label">
                    Coef. {{ coefficientLabel(item.sale_price_coefficient) }}
                  </div>
                </td>
                <td class="text-end">
                  {{ remainingQuantity(item) }}
                </td>
                <td class="text-end">
                  <span v-if="isPromotionLine(item)" class="ticket-price-strike">
                    {{ formatLineAmount(item.base_unit_price || item.unit_price) }}
                  </span>
                  {{ formatLineAmount(item.unit_price) }}
                </td>
                <td class="text-end">
                  {{ formatLineAmount(activeLineTotal(item)) }}
                </td>
              </tr>
            </tbody>
          </table>
  
          <hr class="my-2" />
  
          <!-- Totaux -->
          <div class="ticket-totals">
            <template v-if="sale.wallet_transaction">
              <div class="d-flex justify-content-between"><span>Portefeuille</span><strong>{{ sale.wallet_transaction.wallet?.name || 'Électronique' }}</strong></div>
              <div class="d-flex justify-content-between"><span>Opération</span><strong>{{ walletOperationLabel }}</strong></div>
              <div class="d-flex justify-content-between"><span>Référence</span><strong>{{ sale.wallet_transaction.reference }}</strong></div>
              <div class="d-flex justify-content-between"><span>Solde après</span><strong>{{ $format.currency(sale.wallet_transaction.balance_after) }}</strong></div>
            </template>
            <div class="d-flex justify-content-between">
              <span>Sous-total</span>
              <span>{{ $format.currency(sale.subtotal_amount || sale.total_amount || 0) }}</span>
            </div>
            <div class="d-flex justify-content-between" v-if="Number(sale.discount_amount || 0) > 0">
              <span>Remise</span>
              <span>- {{ $format.currency(sale.discount_amount || 0) }}</span>
            </div>
            <div class="d-flex justify-content-between" v-if="Number(sale.tva_amount || 0) > 0">
              <span>TVA incluse</span>
              <span>{{ $format.currency(sale.tva_amount || 0) }}</span>
            </div>
            <div class="d-flex justify-content-between" v-if="Number(sale.css_amount || 0) > 0">
              <span>CSS incluse</span>
              <span>{{ $format.currency(sale.css_amount || 0) }}</span>
            </div>
            <div class="d-flex justify-content-between">
              <span>Total net</span>
              <strong>{{ $format.currency(sale.total_amount) }}</strong>
            </div>
            <div class="d-flex justify-content-between" v-if="sale.payment_method">
              <span>Paiement</span>
              <span>{{ paymentMethodLabel(sale.payment_method) }}</span>
            </div>
            <div class="d-flex justify-content-between" v-if="sale.payment_method === 'credit'">
              <span>Reste dû</span>
              <span>{{ $format.currency(sale.credit_due_amount || 0) }}</span>
            </div>
            <div class="d-flex justify-content-between" v-if="sale.payment_reference">
              <span>Référence</span>
              <span>{{ sale.payment_reference }}</span>
            </div>
            <div class="d-flex justify-content-between" v-if="sale.payment_method === 'cash' && sale.amount_received != null">
              <span>Reçu</span>
              <span>{{ $format.currency(sale.amount_received) }}</span>
            </div>
            <div class="d-flex justify-content-between" v-if="sale.payment_method === 'cash' && sale.amount_returned != null">
              <span>Rendu</span>
              <span>{{ $format.currency(sale.amount_returned) }}</span>
            </div>
            <div class="d-flex justify-content-between" v-if="sale.customer_name">
              <span>Client</span>
              <span>{{ sale.customer_name }}</span>
            </div>
          </div>
        </div>
  
        <hr class="my-2" />
  
        <!-- Pied de page -->
        <div class="ticket-footer text-center">
          <p class="mb-1">{{ ticketFooterNote }}</p>
          <small>{{ ticketBusinessName }}</small>
          <div v-if="ticketOpeningHours.length" class="ticket-opening-hours">
            <strong>Horaires d’ouverture</strong>
            <span v-for="hours in ticketOpeningHours" :key="hours">{{ hours }}</span>
          </div>
          <div class="ticket-powered-by">powered by Suite241</div>
          <div v-if="ticketSignatureValue" class="ticket-signature">
            <div v-html="ticketSignatureBarcode"></div>
            <small>Signature : {{ ticketSignatureValue }}</small>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script lang="ts">
  import { defineComponent } from 'vue';
  import appLogo from '@/assets/logo.png';
  import { code128Svg, normalizeCode128Value } from '@/support/code128';
  
  interface SaleItem {
    product_name: string;
    unit_price: number;
    base_unit_price?: number;
    sale_price_coefficient?: number | null;
    price_source?: string;
    formula_quantity?: number | null;
    formula_price?: number | null;
    quantity: number;
    total_line: number;
    cancelled_quantity?: number;
  }
  
  interface Sale {
    reference: string;
    daily_position?: number | null;
    ticket_signature_uid?: string | null;
    ticket_last_printed_at?: string | null;
    created_at: string;
    customer_name?: string | null;
    cash_register?: { name?: string | null } | null;
    cashRegister?: { name?: string | null } | null;
    cash_register_session?: { cash_register?: { name?: string | null } | null } | null;
    cashRegisterSession?: { cashRegister?: { name?: string | null } | null } | null;
    subtotal_amount?: number;
    discount_amount?: number;
    tva_amount?: number;
    css_amount?: number;
    tax_amount?: number;
    total_amount: number;
    payment_method?: string | null;
    wallet_transaction?: {
      type?: string;
      reference?: string;
      balance_after?: number;
      wallet?: { name?: string };
    } | null;
    payment_reference?: string | null;
    amount_received?: number | null;
    change_due?: number | null;
    amount_returned?: number | null;
    credit_due_amount?: number | null;
    credit_status?: string | null;
    items: SaleItem[];
  }

  interface TicketShopInfo {
    name?: string | null;
    legal_name?: string | null;
    address?: string | null;
    city?: string | null;
    country?: string | null;
    phone?: string | null;
    email?: string | null;
    tax_id?: string | null;
    logo_url?: string | null;
    main_logo_url?: string | null;
    is_main?: boolean | null;
    footer_note?: string | null;
    timezone?: string | null;
    opening_hours?: Record<string, { is_open?: boolean; opens_at?: string; closes_at?: string }> | null;
  }
  
  export default defineComponent({
    name: 'SaleTicket',
    props: {
      sale: {
        type: Object as () => Sale,
        required: true,
      },
      businessName: {
        type: String,
        default: 'Suite241 Gestion Commerciale - Boutique',
      },
      businessAddress: {
        type: String,
        default: 'Libreville, Gabon',
      },
      businessPhone: {
        type: String,
        default: '+241 xx xx xx xx',
      },
      cashierName: {
        type: String,
        default: '',
      },
      logoText: {
        type: String,
        default: 'LOGO',
      },
      shopInfo: {
        type: Object as () => TicketShopInfo,
        default: () => ({}),
      },
      duplicate: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        hasTicketLogoError: false,
      };
    },
    computed: {
      ticketBusinessName(): string {
        return this.shopInfo.name || this.businessName;
      },
      ticketLegalName(): string {
        return this.shopInfo.legal_name || '';
      },
      ticketAddress(): string {
        const parts = [this.shopInfo.address, this.shopInfo.city, this.shopInfo.country].filter(Boolean);
        return parts.length ? parts.join(', ') : this.businessAddress;
      },
      ticketPhone(): string {
        return this.shopInfo.phone || this.businessPhone;
      },
      ticketEmail(): string {
        return this.shopInfo.email || '';
      },
      ticketTaxId(): string {
        return this.shopInfo.tax_id || '';
      },
      ticketLogoSource(): string {
        return this.shopInfo.main_logo_url || (this.shopInfo.is_main ? this.shopInfo.logo_url : '') || '';
      },
      ticketLogo(): string {
        return this.hasTicketLogoError ? appLogo : this.ticketLogoSource || appLogo;
      },
      ticketFooterNote(): string {
        return this.shopInfo.footer_note || 'Merci pour votre achat !';
      },
      walletOperationLabel(): string {
        const labels: Record<string, string> = { recharge: 'Recharge', deposit: 'Dépôt', withdrawal: 'Retrait' };
        const operationType = this.sale.wallet_transaction?.type;

        return operationType ? labels[operationType] || 'Opération électronique' : 'Opération électronique';
      },
      cashRegisterName(): string {
        return this.sale.cash_register?.name
          || this.sale.cashRegister?.name
          || this.sale.cash_register_session?.cash_register?.name
          || this.sale.cashRegisterSession?.cashRegister?.name
          || '';
      },
      activeItems(): SaleItem[] {
        return (this.sale.items || []).filter((item) => this.remainingQuantity(item) > 0);
      },
      ticketSignatureValue(): string {
        return normalizeCode128Value(this.sale.ticket_signature_uid || '');
      },
      ticketSignatureBarcode(): string {
        return code128Svg(this.ticketSignatureValue);
      },
      ticketOpeningHours(): string[] {
        const days = [['monday', 'Lun'], ['tuesday', 'Mar'], ['wednesday', 'Mer'], ['thursday', 'Jeu'], ['friday', 'Ven'], ['saturday', 'Sam'], ['sunday', 'Dim']];
        const groups: Array<{ first: string; last: string; hours: string }> = [];
        days.forEach(([key, label]) => {
          const day = this.shopInfo.opening_hours?.[key];
          if (!day?.is_open || !day.opens_at || !day.closes_at) return;
          const hours = `${day.opens_at}–${day.closes_at}`;
          const previous = groups[groups.length - 1];
          if (previous?.hours === hours) previous.last = label;
          else groups.push({ first: label, last: label, hours });
        });
        return groups.map((group) => `${group.first}${group.first !== group.last ? `–${group.last}` : ''} : ${group.hours}`);
      },
      isCreditTicket(): boolean {
        return this.sale.payment_method === 'credit'
          && Number(this.sale.credit_due_amount || 0) > 0;
      },
    },
    watch: {
      ticketLogoSource(): void {
        this.hasTicketLogoError = false;
      },
    },
    methods: {
      formatLineAmount(value: number): string {
        return new Intl.NumberFormat('fr-FR', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 0,
        }).format(Number(value || 0));
      },
      formatPrintedTime(value: string): string {
        return new Intl.DateTimeFormat('fr-FR', { timeStyle: 'short', timeZone: this.shopInfo.timezone || undefined }).format(new Date(value));
      },
      remainingQuantity(item: SaleItem): number {
        return Math.max(Number(item.quantity || 0) - Number(item.cancelled_quantity || 0), 0);
      },
      activeLineTotal(item: SaleItem): number {
        const quantity = this.remainingQuantity(item);
        const formulaQuantity = Number(item.formula_quantity || 0);
        const formulaPrice = Number(item.formula_price || 0);

        if (this.isFormulaLine(item) && formulaQuantity >= 2 && formulaPrice > 0) {
          return (Math.floor(quantity / formulaQuantity) * formulaPrice)
            + ((quantity % formulaQuantity) * Number(item.unit_price || 0));
        }

        return quantity * Number(item.unit_price || 0);
      },
      isFormulaLine(item: SaleItem): boolean {
        return String(item.price_source || '') === 'formula';
      },
      isPromotionLine(item: SaleItem): boolean {
        return Number(item.base_unit_price || 0) > Number(item.unit_price || 0)
          || ['planned_promotion', 'cashier_promotion'].includes(String(item.price_source || ''));
      },
      coefficientLabel(value: number | null | undefined): string {
        if (value === null || value === undefined) {
          return '—';
        }

        const coefficient = Number(value);
        return Number.isFinite(coefficient) ? `x${coefficient.toFixed(2)}` : '—';
      },
      paymentMethodLabel(method: string): string {
        return {
          cash: 'Espèces',
          credit: 'Crédit',
          mobile_money: 'Mobile money',
          card: 'Carte',
        }[method] || method;
      },
      handleTicketLogoError(): void {
        if (this.ticketLogo !== appLogo) {
          this.hasTicketLogoError = true;
        }
      },
    },
  });
  </script>
  
  <style scoped>
  .ticket-wrapper {
    display: flex;
    justify-content: center;
  }
  
  /* Largeur type ticket 80mm */
  .ticket {
    width: 80mm;
    max-width: 100%;
    background: #ffffff;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 8px 10px;
    font-size: 11px;
    color: #000000;
    font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
    font-weight: 600;
    line-height: 1.28;
    overflow: hidden;
    position: relative;
  }

  .ticket-watermark {
    color: rgba(220, 53, 69, 0.14);
    font-size: 24px;
    font-weight: 900;
    left: 50%;
    letter-spacing: 0.08em;
    position: absolute;
    text-transform: uppercase;
    top: 50%;
    transform: translate(-50%, -50%) rotate(-24deg);
    white-space: nowrap;
    z-index: 0;
  }

  .ticket-watermark--credit {
    color: rgba(14, 116, 144, 0.18);
    top: 56%;
  }

  .ticket > *:not(.ticket-watermark) {
    position: relative;
    z-index: 1;
  }
  
  .ticket-header {
    margin-bottom: 4px;
  }
  
  .ticket-logo {
    font-weight: 800;
    font-size: 12px;
    border: 1px solid #000000;
    display: inline-block;
    padding: 2px 6px;
    border-radius: 3px;
    margin-bottom: 4px;
  }

  .ticket-logo-image {
    display: block;
    max-height: 42px;
    max-width: 48mm;
    object-fit: contain;
    margin: 0 auto 4px;
  }

  .ticket-legal-name {
    font-size: 10px;
    font-weight: 700;
  }
  
  .ticket-title {
    font-size: 12px;
    font-weight: 800;
  }
  
  .ticket-subtitle {
    font-size: 10px;
  }
  
  .ticket-meta {
    font-size: 10px;
  }
  
  .ticket-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
  }
  
  .ticket-table th,
  .ticket-table td {
    padding: 2px 0;
  }
  
  .ticket-table th {
    border-bottom: 1px solid #000000;
    font-weight: 800;
  }
  
  .ticket-table td {
    vertical-align: top;
  }

  .ticket-promo-label {
    color: #198754;
    font-size: 9px;
    font-weight: 700;
  }

  .ticket-coefficient-label {
    color: #000000;
    font-size: 9px;
    font-weight: 600;
  }

  .ticket-price-strike {
    color: #000000;
    display: block;
    font-size: 9px;
    text-decoration: line-through;
  }
  
  .text-start {
    text-align: left;
  }
  .text-end {
    text-align: right;
  }
  
  .ticket-totals {
    font-size: 11px;
    font-weight: 700;
  }
  
  .ticket-footer {
    font-size: 10px;
  }

  .ticket-powered-by {
    margin-top: 8px;
    font-size: 8px;
    text-align: center;
  }

  .ticket-signature {
    margin: 0.6rem auto 0;
    max-width: 63mm;
  }

  .ticket-signature :deep(svg) {
    display: block;
    height: 13mm;
    margin: 0 auto 0.2rem;
    max-width: 100%;
  }

  .ticket-signature small {
    font-size: 7px;
    overflow-wrap: anywhere;
  }
  .ticket-opening-hours { display: grid; font-size: 9px; gap: 0.12rem; margin: 0.5rem 0; }
  .ticket-opening-hours strong { font-size: 9px; }

  @media print {
    .ticket-wrapper,
    .ticket,
    .ticket * {
      color: #000000 !important;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }

    .ticket {
      border: 0;
      box-shadow: none;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.3;
      text-shadow: 0 0 0 #000000;
    }

    .ticket-logo {
      border-color: #000000;
      font-weight: 900;
    }

    .ticket-title {
      font-size: 13px;
      font-weight: 900;
    }

    .ticket-table {
      font-size: 11px;
    }

    .ticket-table th {
      border-bottom: 1px solid #000000;
      font-weight: 900;
    }

    .ticket-table th,
    .ticket-table td {
      padding: 2px 0;
    }

    .ticket-totals,
    .ticket-totals strong {
      font-weight: 900;
    }

    hr {
      border-top: 1px solid #000000;
      opacity: 1;
    }
  }
  </style>
  
